/**
 * Cargar contactos con consentimiento (`/activa/publico/cargas/nueva`, y `?carga=:id` para retomar un borrador): se
 * abre desde la pestaña «Con consentimiento» de Público.
 *
 * - Asistente de 4 pasos (`subir`, `vincular`, `consentimiento`, `revisar`) y la pantalla de importación
 *   (`resultado`). Los pasos van POR NOMBRE en `?paso=` (misma regla que el asistente de campaña): el de
 *   la URL se respeta sólo si es alcanzable con lo que ya se completó.
 * - El borrador vive en el servidor (la carga en estado `borrador`, con el archivo adentro durante 24 h).
 *   Un F5 lo retoma con `GET /cargas/:id` y `GET /cargas/:id/columnas`; si venció, el backend contesta
 *   404 y se vuelve a «Subir» con el aviso.
 * - Lo que se va a importar lo calcula el backend (`previsualizar`) con la misma función que la
 *   importación. «Importar» manda la huella de esa revisión: si algo cambió desde entonces, 409
 *   `CARGA_DESACTUALIZADA` y se vuelve a revisar sola. El botón sólo se habilita si la revisión en
 *   pantalla es la de lo que está elegido ahora (la firma de la entrada).
 * - Una carga `fallida` sólo se reintenta tal cual: no se puede volver a Vincular ni a Consentimiento.
 * - `?volverA=` es la ruta del asistente de campaña que mandó a subir el Excel (pestaña «Subir un Excel» del paso
 *   Audiencia, 26/10/2026). Se conserva en cada paso; al terminar, «Usar en la campaña» pide el público de la carga
 *   (`POST /cargas/:id/publico`) y vuelve con `&publico=:id`. Así, además, se importa aunque todos ya estuvieran en la
 *   base: la importación les suma la carga, y es lo que el público de la carga mira.
 */

import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import type { DragEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, FileSpreadsheet, Loader2, Upload } from "lucide-react";
import type {
  CargaPublica, ColumnasCarga, EntradaPrevisualizar, Previsualizacion, PublicoDetalle, RespuestaCrearCarga, VinculoColumna,
} from "../../types.base";
import { baseApi } from "../../baseApi";
import { useCuentaOficial } from "../../contexto";
import { usePolling } from "../../hooks/usePolling";
import {
  TEXTO_DECLARACION_INCOMPLETA, hoyLocal, nombreDeCarga, validarArchivoLocal, validarDeclaracionLocal, validarVinculoLocal, vinculoInicial,
} from "../../logic/base";
import { prepararCarga } from "../../logic/archivo";
import WizardStepper from "../../../campana-nueva/components/WizardStepper";
import type { Fase, Paso } from "../../../campana-nueva/components/WizardStepper";
import { num } from "../../../campana-nueva/utils/format";
import {
  AvisoAmbar, CajaError, Esqueleto, R_CARGAS, R_CARGAS_LISTA, descargarErroresCarga, erroresDe, fuenteElegida, llamar, separarFuente, textoFallo,
} from "../../components/base/comun";
import { TablaVinculo } from "../../components/base/TablaVinculo";
import { DECLARACION_VACIA, FormConsentimiento } from "../../components/base/FormConsentimiento";
import type { EstadoDeclaracion } from "../../components/base/FormConsentimiento";
import { ConteosCarga } from "../../components/base/ConteosCarga";
import { ProgresoCarga } from "../../components/base/ProgresoCarga";
import { useResumenAlTerminar } from "./BaseLayout";
import { rutaDeVuelta, volverASeguro } from "./PublicoEditorPage";

const PASOS = ["subir", "vincular", "consentimiento", "revisar"] as const;
type PasoWizard = (typeof PASOS)[number];
type PasoCarga = PasoWizard | "resultado";

const STEPS: Paso[] = [
  { key: "subir", label: "Subir el archivo", desc: "Un Excel o un CSV con los teléfonos." },
  { key: "vincular", label: "Vincular columnas", desc: "Qué es cada columna del archivo." },
  { key: "consentimiento", label: "Consentimiento", desc: "Quién aceptó recibir mensajes, y las etiquetas de la carga." },
  { key: "revisar", label: "Revisar", desc: "Cómo queda la base antes de importar." },
];
const FASES: Fase[] = [
  { key: "archivo", label: "El archivo", pasos: [0, 1] },
  { key: "consentimiento", label: "El consentimiento", pasos: [2] },
  { key: "revision", label: "La revisión", pasos: [3] },
];

const TEXTO_VENCIDA = "Esta carga venció sin importarse: subí el archivo de nuevo";

const esPasoWizard = (p: string | null): p is PasoWizard => !!p && (PASOS as readonly string[]).includes(p);

export default function CargaNuevaPage() {
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const { config } = useCuentaOficial();
  // Sin config (todavía cargando o falló) se asume lo más estricto: que los números sin código de país se excluyen.
  const excluyeAmbiguos = config?.excluirTelefonosAmbiguos !== false;
  const idCarga = sp.get("carga") || "";
  const pasoPedido = sp.get("paso");
  const volverA = volverASeguro(sp.get("volverA"));
  /** Los parámetros nuevos de la URL, con la vuelta a la campaña si la hay. */
  const conVuelta = useCallback((p: Record<string, string>) => new URLSearchParams(volverA ? { ...p, volverA } : p), [volverA]);
  const hoy = hoyLocal();

  // ── La carga ──────────────────────────────────────────────────────────
  const [carga, setCarga] = useState<CargaPublica | null>(null);
  const [cols, setCols] = useState<ColumnasCarga | null>(null);
  const [cargandoCarga, setCargandoCarga] = useState(false);
  const [errorCarga, setErrorCarga] = useState("");
  const [reintentoCarga, setReintentoCarga] = useState(0);
  const [avisoVencida, setAvisoVencida] = useState(false);

  // ── Paso 1 ────────────────────────────────────────────────────────────
  const [archivo, setArchivo] = useState<File | null>(null);
  const [nombre, setNombre] = useState("");
  const [errorArchivo, setErrorArchivo] = useState("");
  const [subiendo, setSubiendo] = useState(false);
  const [errorSubir, setErrorSubir] = useState("");
  const [arrastrando, setArrastrando] = useState(false);

  // ── Pasos 2 y 3 ───────────────────────────────────────────────────────
  const [vinculo, setVinculo] = useState<VinculoColumna[]>([]);
  const [celularesArgentinos, setCelularesArgentinos] = useState(false);
  const [declaracion, setDeclaracion] = useState<EstadoDeclaracion>(DECLARACION_VACIA);
  const [etiquetas, setEtiquetas] = useState<string[]>([]);
  const [sugerencias, setSugerencias] = useState<{ clave: string; texto: string }[]>([]);

  // ── Paso 4 ────────────────────────────────────────────────────────────
  const [previa, setPrevia] = useState<Previsualizacion | null>(null);
  const [previaFirma, setPreviaFirma] = useState("");
  const [calculando, setCalculando] = useState(false);
  const [errorPrevia, setErrorPrevia] = useState("");
  const [erroresMapeo, setErroresMapeo] = useState<string[]>([]);
  const [avisoRecalculo, setAvisoRecalculo] = useState("");
  const [importando, setImportando] = useState(false);
  const [errorImportar, setErrorImportar] = useState("");
  const seqPrevia = useRef(0);

  // ── Resultado ─────────────────────────────────────────────────────────
  const [reintentando, setReintentando] = useState(false);
  const [errorReintento, setErrorReintento] = useState("");
  const [archivoVencido, setArchivoVencido] = useState(false);
  const [descargando, setDescargando] = useState(false);
  const [errorDescarga, setErrorDescarga] = useState("");
  const [usando, setUsando] = useState(false);
  const [errorUsar, setErrorUsar] = useState("");

  // ── Validaciones y paso actual ────────────────────────────────────────
  const catalogo = useMemo(() => cols?.catalogo || [], [cols]);
  const vVinculo = useMemo(() => validarVinculoLocal(vinculo, catalogo), [vinculo, catalogo]);
  const fuente = fuenteElegida(declaracion.opcion, declaracion.otra);
  const vDecl = validarDeclaracionLocal({ activa: declaracion.activa, fuente, fecha: declaracion.fecha, constancia: declaracion.constancia }, hoy);

  // Lo que se manda a previsualizar y su firma: la revisión en pantalla vale sólo si es la de esta entrada.
  const entrada: EntradaPrevisualizar = { vinculo, declaracion: vDecl.ok ? vDecl.declaracion : null, etiquetas, celularesArgentinos };
  const firma = JSON.stringify(entrada);

  const maxPaso = !carga || !cols || carga.estado !== "borrador" ? 0 : !vVinculo.ok ? 1 : !vDecl.ok ? 2 : 3;
  let paso: PasoCarga;
  if (pasoPedido === "resultado" && carga && carga.estado !== "borrador") paso = "resultado";
  else if (esPasoWizard(pasoPedido) && PASOS.indexOf(pasoPedido) <= maxPaso) paso = pasoPedido;
  else paso = carga && cols && carga.estado === "borrador" ? PASOS[Math.min(maxPaso, 1)] : "subir";
  const iPaso = paso === "resultado" ? PASOS.length : PASOS.indexOf(paso);

  const irA = useCallback((p: PasoCarga, extra: Record<string, string> = {}, reemplazar = false) => {
    setSp((prev) => {
      const n = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(extra)) n.set(k, v);
      n.set("paso", p);
      return n;
    }, { replace: reemplazar });
  }, [setSp]);

  // ── Aplicar una carga (recién subida o retomada) ──────────────────────
  const aplicar = useCallback((c: CargaPublica, columnas: ColumnasCarga) => {
    setCarga(c);
    setCols(columnas);
    const nombresColumnas = (columnas.columnas || []).map((x) => x.columna);
    setVinculo(vinculoInicial(nombresColumnas.length ? nombresColumnas : c.columnas || [], columnas.sugerencia?.vinculo || [], c.vinculo));
    setCelularesArgentinos(!!c.celularesArgentinos);
    setEtiquetas((c.etiquetas || []).map((e) => e.texto));
    setDeclaracion(c.declaracion
      ? { activa: true, ...separarFuente(c.declaracion.fuente), fecha: c.declaracion.fecha, constancia: true }
      : DECLARACION_VACIA);
    setPrevia(null);
    setPreviaFirma("");
  }, []);

  const limpiar = useCallback(() => {
    setCarga(null); setCols(null); setVinculo([]); setCelularesArgentinos(false); setDeclaracion(DECLARACION_VACIA); setEtiquetas([]);
    setPrevia(null); setPreviaFirma(""); setErrorPrevia(""); setErroresMapeo([]); setAvisoRecalculo(""); setErrorImportar("");
    setArchivo(null); setNombre(""); setErrorArchivo(""); setErrorSubir(""); setErrorCarga("");
    setReintentando(false); setErrorReintento(""); setArchivoVencido(false); setErrorDescarga("");
  }, []);

  /** El borrador venció (404): de vuelta a «Subir» con el aviso. */
  const vencida = useCallback(() => {
    limpiar();
    setAvisoVencida(true);
    setSp(conVuelta({ paso: "subir" }), { replace: true });
  }, [limpiar, setSp, conVuelta]);

  // Retomar `?carga=`: la carga y, si es borrador, sus columnas (ejemplos, ambigüedades y catálogo).
  useEffect(() => {
    if (!idCarga || carga?.id === idCarga) return;
    let vivo = true;
    setCargandoCarga(true);
    setErrorCarga("");
    void (async () => {
      const l = await llamar<CargaPublica>(() => baseApi.carga(idCarga));
      if (!vivo) return;
      if (!l.ok || !l.data) {
        setCargandoCarga(false);
        if (!l.ok && l.status === 404) vencida();
        else setErrorCarga(l.ok ? "El servidor no devolvió la carga." : textoFallo(l));
        return;
      }
      const c = l.data;
      if (c.estado !== "borrador") {
        if (pasoPedido === "resultado") { setCarga(c); setCargandoCarga(false); return; }
        navigate(`${R_CARGAS}/${encodeURIComponent(c.id)}`, { replace: true });
        return;
      }
      const lc = await llamar<ColumnasCarga>(() => baseApi.columnasCarga(idCarga));
      if (!vivo) return;
      setCargandoCarga(false);
      if (!lc.ok || !lc.data) {
        if (!lc.ok && lc.status === 404) vencida();
        else if (!lc.ok && lc.code === "CARGA_NO_EDITABLE") navigate(`${R_CARGAS}/${encodeURIComponent(c.id)}`, { replace: true });
        else setErrorCarga(lc.ok ? "El servidor no devolvió las columnas." : textoFallo(lc));
        return;
      }
      aplicar(c, lc.data);
    })();
    return () => { vivo = false; };
    // Sólo cuando cambia el id pedido (o se reintenta): la carga recién subida ya está en el estado.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idCarga, reintentoCarga]);

  // Las etiquetas que ya existen, para el autocompletado. Si falla, se escribe sin sugerencias.
  useEffect(() => {
    void (async () => {
      const l = await llamar<{ etiquetas: { clave: string; texto: string }[] }>(() => baseApi.opciones());
      if (l.ok && Array.isArray(l.data?.etiquetas)) setSugerencias(l.data.etiquetas);
    })();
  }, []);

  // ── Previsualizar ─────────────────────────────────────────────────────
  const previsualizar = useCallback(async (e: EntradaPrevisualizar, f: string) => {
    if (!carga) return;
    const mio = ++seqPrevia.current;
    setCalculando(true);
    setErrorPrevia("");
    setErroresMapeo([]);
    const l = await llamar<Previsualizacion>(() => baseApi.previsualizar(carga.id, e));
    // Una respuesta vieja (se cambió algo mientras calculaba) no pisa la nueva.
    if (mio !== seqPrevia.current) return;
    setCalculando(false);
    if (!l.ok || !l.data) {
      setPrevia(null);
      setPreviaFirma("");
      if (!l.ok && l.status === 404) { vencida(); return; }
      if (!l.ok && l.code === "CARGA_NO_EDITABLE") { navigate(`${R_CARGAS}/${encodeURIComponent(carga.id)}`, { replace: true }); return; }
      if (!l.ok && l.code === "MAPEO_COLUMNAS_INVALIDO") { const errs = erroresDe(l); setErroresMapeo(errs.length ? errs : [textoFallo(l)]); return; }
      setErrorPrevia(l.ok ? "El servidor no devolvió la revisión." : textoFallo(l));
      return;
    }
    setPrevia(l.data);
    setPreviaFirma(f);
    if (l.data.carga) setCarga(l.data.carga);
  }, [carga, navigate, vencida]);

  const columnaConsentimiento = vinculo.find((v) => v.destino === "consentimiento")?.columna ?? null;
  const tituloColumnaConsentimiento = columnaConsentimiento
    ? cols?.columnas.find((c) => c.columna === columnaConsentimiento)?.titulo || columnaConsentimiento
    : null;

  // Paso 3: al entrar, si hay columna de consentimiento, se lee qué dice (columnaConsentimiento no depende de la declaración).
  useEffect(() => {
    if (paso !== "consentimiento" || !columnaConsentimiento || !carga) return;
    if (previa && previaFirma === firma) return;
    void previsualizar(entrada, firma);
    // Sólo al entrar al paso: mientras se escribe la declaración no se vuelve a pedir.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paso, carga?.id]);

  // Paso 4: al entrar y cada vez que se vuelve con cambios.
  useEffect(() => {
    if (paso !== "revisar" || !carga || previaFirma === firma) return;
    void previsualizar(entrada, firma);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paso, firma, carga?.id]);

  // ── Subir ─────────────────────────────────────────────────────────────
  const elegirArchivo = (f: File | null | undefined) => {
    setErrorSubir("");
    if (!f) return;
    const v = validarArchivoLocal(f.name, f.size);
    if (!v.ok) { setArchivo(null); setErrorArchivo(v.error); return; }
    setErrorArchivo("");
    setArchivo(f);
    setNombre(nombreDeCarga(f.name));
  };

  const subir = async () => {
    if (!archivo || subiendo) return;
    setSubiendo(true);
    setErrorSubir("");
    let cuerpo;
    try {
      cuerpo = await prepararCarga(archivo, nombre);
    } catch (e) {
      setSubiendo(false);
      setErrorSubir(e instanceof Error && e.message ? e.message : "No pudimos leer el archivo.");
      return;
    }
    const l = await llamar<RespuestaCrearCarga>(() => baseApi.crearCarga(cuerpo));
    setSubiendo(false);
    if (!l.ok || !l.data?.carga) { setErrorSubir(l.ok ? "El servidor no devolvió la carga." : textoFallo(l)); return; }
    const anterior = carga?.estado === "borrador" ? carga.id : "";
    aplicar(l.data.carga, l.data);
    setArchivo(null);
    setAvisoVencida(false);
    setSp(conVuelta({ carga: l.data.carga.id, paso: "vincular" }));
    // El borrador anterior (se eligió otro archivo) no sirve más: se descarta. Si falla, vence solo a las 24 h.
    if (anterior && anterior !== l.data.carga.id) void llamar(() => baseApi.descartarCarga(anterior));
  };

  // ── Importar ──────────────────────────────────────────────────────────
  const revisionVigente = !!previa && previaFirma === firma && !calculando;
  // Para una campaña también cuentan los que ya estaban sin cambios: la importación les suma esta carga.
  const aImportar = previa ? previa.conteos.nuevos + previa.conteos.actualizados + (volverA ? previa.conteos.sinCambios : 0) : 0;

  const importar = async () => {
    if (!carga || !previa || !revisionVigente || importando) return;
    setImportando(true);
    setErrorImportar("");
    setAvisoRecalculo("");
    const l = await llamar<CargaPublica>(() => baseApi.importar(carga.id, previa.huella));
    setImportando(false);
    if (!l.ok || !l.data) {
      if (!l.ok && l.code === "CARGA_DESACTUALIZADA") {
        setAvisoRecalculo("Algo cambió desde la revisión: la recalculamos.");
        void previsualizar(entrada, firma);
        return;
      }
      if (!l.ok && l.code === "CARGA_EN_PROCESO") {
        const c = await llamar<CargaPublica>(() => baseApi.carga(carga.id));
        if (c.ok && c.data) { setCarga(c.data); irA("resultado"); return; }
      }
      setErrorImportar(l.ok ? "El servidor no devolvió la carga." : textoFallo(l));
      return;
    }
    setCarga(l.data);
    irA("resultado");
  };

  // ── Resultado ─────────────────────────────────────────────────────────
  const refrescar = useCallback(async () => {
    if (!carga) return;
    const l = await llamar<CargaPublica>(() => baseApi.carga(carga.id));
    if (!l.ok || !l.data) throw new Error(l.ok ? "sin carga" : l.error || "sin respuesta");
    setCarga(l.data);
  }, [carga]);
  const poll = usePolling(refrescar, paso === "resultado" && carga?.estado === "importando" ? 1500 : null, [carga?.id]);
  useResumenAlTerminar(carga?.estado);

  const reintentar = async () => {
    if (!carga || reintentando) return;
    setReintentando(true);
    setErrorReintento("");
    const l = await llamar<CargaPublica>(() => baseApi.importar(carga.id, carga.huellaPrevia || ""));
    setReintentando(false);
    if (!l.ok || !l.data) {
      if (!l.ok && l.code === "ARCHIVO_VENCIDO") { setArchivoVencido(true); return; }
      setErrorReintento(l.ok ? "El servidor no devolvió la carga." : textoFallo(l));
      return;
    }
    setCarga(l.data);
  };

  const descargarErrores = async () => {
    if (!carga || descargando) return;
    setDescargando(true);
    setErrorDescarga(await descargarErroresCarga(carga.id, carga.nombre));
    setDescargando(false);
  };

  const subirOtro = () => {
    limpiar();
    setAvisoVencida(false);
    setSp(conVuelta({}));
  };

  /** «Usar en la campaña»: el público de esta carga (el que ya había o uno nuevo) y de vuelta al asistente. */
  const usarEnCampana = async () => {
    if (!carga || !volverA || usando) return;
    setUsando(true);
    setErrorUsar("");
    const l = await llamar<PublicoDetalle>(() => baseApi.publicoDeCarga(carga.id));
    setUsando(false);
    if (!l.ok || !l.data?.id) { setErrorUsar(l.ok ? "El servidor no devolvió el público." : textoFallo(l)); return; }
    navigate(rutaDeVuelta(volverA, l.data.id));
  };

  // ── Navegación del asistente ──────────────────────────────────────────
  const siguiente = (() => {
    switch (paso) {
      case "vincular": return { label: "Siguiente", disabled: !vVinculo.ok, motivo: vVinculo.errores[0] || "", onClick: () => irA("consentimiento") };
      case "consentimiento": return { label: "Siguiente", disabled: !vDecl.ok, motivo: vDecl.ok ? "" : TEXTO_DECLARACION_INCOMPLETA, onClick: () => irA("revisar") };
      case "revisar": {
        const motivo = calculando ? "Calculando la revisión…" : !revisionVigente ? "Falta la revisión." : aImportar === 0 ? "No hay nada nuevo para importar" : "";
        return { label: `Importar ${num(aImportar)} ${aImportar === 1 ? "contacto" : "contactos"}`, disabled: !!motivo || importando, motivo, onClick: importar };
      }
      default: return null;
    }
  })();
  const atras = () => {
    const i = PASOS.indexOf(paso as PasoWizard);
    if (i > 0) irA(PASOS[i - 1]);
  };

  // ── Render ────────────────────────────────────────────────────────────
  const volver = (
    <Link to={volverA || R_CARGAS_LISTA} className="text-xs font-semibold text-slate-500 hover:text-slate-700 inline-flex items-center gap-1">
      <ArrowLeft size={13} /> {volverA ? "Volver a la campaña" : "Contactos con consentimiento"}
    </Link>
  );

  if (idCarga && cargandoCarga && !carga) {
    return <div className="space-y-4">{volver}<Esqueleto filas={3} alto="h-24" /></div>;
  }
  if (idCarga && errorCarga && !carga) {
    return (
      <div className="space-y-4">
        {volver}
        <CajaError titulo="No pudimos traer la carga." detalle={errorCarga} onReintentar={() => setReintentoCarga((n) => n + 1)} />
      </div>
    );
  }

  if (paso === "resultado" && carga) {
    return (
      <div className="space-y-5">
        {volver}
        <section className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm space-y-4">
          <header>
            <h3 className="text-lg font-semibold text-slate-900 tracking-tight">{carga.nombre}</h3>
            <p className="text-sm text-slate-500">{carga.nombreArchivo}</p>
          </header>
          <ProgresoCarga
            carga={carga}
            onReintentar={reintentar}
            reintentando={reintentando}
            errorReintento={errorReintento}
            archivoVencido={archivoVencido}
            onDescargarErrores={descargarErrores}
            descargando={descargando}
            errorDescarga={errorDescarga}
            onSubirOtro={subirOtro}
            pollDetenido={poll.detenido}
            onReintentarPoll={poll.reintentar}
            usarEnCampana={volverA ? { onUsar: () => void usarEnCampana(), usando, error: errorUsar } : undefined}
          />
          {!["importando", "importada", "fallida"].includes(carga.estado) && (
            <p className="text-sm text-slate-600">
              Esta carga ya no se está importando. <Link to={`${R_CARGAS}/${encodeURIComponent(carga.id)}`} className="font-semibold underline">Ver la carga</Link>
            </p>
          )}
          {carga.estado === "fallida" && (
            <p className="text-sm text-slate-600">
              <Link to={`${R_CARGAS}/${encodeURIComponent(carga.id)}`} className="font-semibold underline">Ir a la pantalla de la carga</Link>
            </p>
          )}
        </section>
      </div>
    );
  }

  const paso0 = STEPS[iPaso] ?? STEPS[0];

  return (
    <div className="space-y-5">
      {volver}

      <WizardStepper steps={STEPS} fases={FASES} current={iPaso} maxReached={maxPaso}
        onGo={(i) => { if (i <= maxPaso) irA(PASOS[i]); }} />

      <section className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm space-y-5">
        {/* Sin «Paso N de M» acá: ya lo dice el WizardStepper de arriba. */}
        <header>
          <h3 className="text-lg font-semibold text-slate-900 tracking-tight">{paso0.label}</h3>
          <p className="text-sm text-slate-500">{paso0.desc}</p>
        </header>

        {paso === "subir" && (
          <PasoSubir
            avisoVencida={avisoVencida}
            carga={carga && cols ? carga : null}
            archivo={archivo}
            nombre={nombre}
            onNombre={setNombre}
            onArchivo={elegirArchivo}
            errorArchivo={errorArchivo}
            errorSubir={errorSubir}
            subiendo={subiendo}
            onSubir={subir}
            onSeguir={() => irA("vincular")}
            arrastrando={arrastrando}
            onArrastrando={setArrastrando}
          />
        )}

        {paso === "vincular" && cols && (
          <div className="space-y-4">
            <TablaVinculo columnas={cols.columnas} vinculo={vinculo} sugerencia={cols.sugerencia} catalogo={catalogo}
              errores={vVinculo.porColumna} onChange={setVinculo} />
            <CasillaArgentinos valor={celularesArgentinos} onChange={setCelularesArgentinos} excluyeAmbiguos={excluyeAmbiguos} />
          </div>
        )}

        {paso === "consentimiento" && (
          <FormConsentimiento
            columna={tituloColumnaConsentimiento}
            conteoColumna={calculando ? null : previa?.conteos.columnaConsentimiento ?? null}
            contandoColumna={calculando}
            errorColumna={erroresMapeo.length ? erroresMapeo.join(" ") : errorPrevia}
            onReintentarColumna={() => void previsualizar(entrada, firma)}
            declaracion={declaracion}
            onDeclaracion={setDeclaracion}
            etiquetas={etiquetas}
            onEtiquetas={setEtiquetas}
            sugerencias={sugerencias}
            hoy={hoy}
          />
        )}

        {paso === "revisar" && (
          <div className="space-y-4">
            {avisoRecalculo && <AvisoAmbar>{avisoRecalculo}</AvisoAmbar>}
            <ConteosCarga
              previa={previa}
              calculando={calculando}
              error={errorPrevia}
              erroresMapeo={erroresMapeo}
              onReintentar={() => void previsualizar(entrada, firma)}
              onDescargarErrores={descargarErrores}
              descargando={descargando}
              errorDescarga={errorDescarga}
            />
            {errorImportar && <p className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">{errorImportar}</p>}
          </div>
        )}

        {paso !== "subir" && (
          <footer className="flex items-center justify-between gap-3 pt-4 border-t border-slate-100 flex-wrap">
            <button type="button" onClick={atras}
              className="h-10 px-4 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-50">
              Atrás
            </button>
            {siguiente && (
              <div className="text-right">
                <button type="button" onClick={siguiente.onClick} disabled={siguiente.disabled}
                  className="h-10 px-5 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
                  {paso === "revisar" && importando && <Loader2 size={14} className="animate-spin" />}
                  {siguiente.label}
                </button>
                {siguiente.disabled && siguiente.motivo && <p className="text-xs text-slate-500 mt-1.5 max-w-sm ml-auto">{siguiente.motivo}</p>}
              </div>
            )}
          </footer>
        )}
      </section>
    </div>
  );
}

function CasillaArgentinos({ valor, onChange, excluyeAmbiguos }: { valor: boolean; onChange: (v: boolean) => void; excluyeAmbiguos: boolean }) {
  const id = useId();
  return (
    <div className="rounded-xl border border-slate-100 p-4">
      <label htmlFor={id} className="flex items-start gap-2 text-sm font-semibold text-slate-900 cursor-pointer">
        <input id={id} type="checkbox" checked={valor} onChange={(e) => onChange(e.target.checked)}
          className="mt-0.5 rounded border-slate-300 text-violet-600 focus:ring-violet-500" />
        Los números sin código de país son celulares de Argentina
      </label>
      <p className="text-xs text-slate-500 mt-1 ml-6">
        {"Marcalo si sabés que todos los números son celulares argentinos (por ejemplo, \"11 5544-3322\")."}
        {excluyeAmbiguos && " Si no, se importan igual pero, con la configuración actual, no reciben campañas hasta que se revisen."}
      </p>
    </div>
  );
}

function PasoSubir(p: {
  avisoVencida: boolean; carga: CargaPublica | null; archivo: File | null; nombre: string; onNombre: (s: string) => void;
  onArchivo: (f: File | null | undefined) => void; errorArchivo: string; errorSubir: string; subiendo: boolean;
  onSubir: () => void; onSeguir: () => void; arrastrando: boolean; onArrastrando: (b: boolean) => void;
}) {
  const idInput = useId();
  const idNombre = useId();
  const onDrop = (e: DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    p.onArrastrando(false);
    p.onArchivo(e.dataTransfer?.files?.[0]);
  };
  return (
    <div className="space-y-4">
      {p.avisoVencida && <AvisoAmbar>{TEXTO_VENCIDA}</AvisoAmbar>}
      {p.carga && (
        <div className="flex items-center justify-between gap-3 flex-wrap rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-700">
          <span>{`Ya subiste "${p.carga.nombreArchivo}". Si subís otro archivo, este borrador se descarta.`}</span>
          <button type="button" onClick={p.onSeguir} className="h-9 px-3 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700">
            Seguir con este archivo
          </button>
        </div>
      )}

      <label htmlFor={idInput}
        onDragOver={(e) => { e.preventDefault(); p.onArrastrando(true); }}
        onDragLeave={() => p.onArrastrando(false)}
        onDrop={onDrop}
        className={`block rounded-2xl border-2 border-dashed p-8 text-center cursor-pointer transition-colors ${
          p.arrastrando ? "border-violet-400 bg-violet-50" : "border-slate-200 hover:border-slate-300"
        }`}>
        <FileSpreadsheet size={26} className="mx-auto text-slate-400" />
        <p className="mt-2 text-sm font-semibold text-slate-900">{p.archivo ? p.archivo.name : "Soltá el archivo acá o hacé clic para elegirlo"}</p>
        <p className="mt-1 text-xs text-slate-500">
          Subí un Excel (.xlsx o .xls) o un CSV. Se lee sólo la primera hoja. Hasta 50.000 filas. Tamaño máximo: 5,9 MB un Excel, 8 MB un CSV.
        </p>
        <input id={idInput} type="file" accept=".xlsx,.xls,.csv" className="sr-only" aria-label="Archivo"
          onChange={(e) => { p.onArchivo(e.target.files?.[0]); e.target.value = ""; }} />
      </label>
      {p.errorArchivo && <p className="text-sm text-red-600" role="alert">{p.errorArchivo}</p>}

      <div className="max-w-md">
        <label htmlFor={idNombre} className="text-xs font-semibold text-slate-600">Nombre de la carga</label>
        <input id={idNombre} value={p.nombre} maxLength={120} onChange={(e) => p.onNombre(e.target.value.slice(0, 120))}
          className="w-full mt-1.5 h-10 rounded-xl border border-slate-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
      </div>

      {p.errorSubir && <p className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">{p.errorSubir}</p>}

      <button type="button" onClick={p.onSubir} disabled={!p.archivo || p.subiendo}
        className="h-10 px-5 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
        {p.subiendo ? <><Loader2 size={14} className="animate-spin" /> Leyendo el archivo…</> : <><Upload size={14} /> Subir y leer</>}
      </button>
    </div>
  );
}
